'use client';

import Link from '@eliadmin/core/src/components/link';
import { useState, useRef } from 'react';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { SheetClose } from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const MobileSidebarDesktopContent = ({ categories }: { categories: CategoriesEntity[] }) => {
  const [activeParent, setActiveParent] = useState<string | null>(null);
  const [activeChild, setActiveChild] = useState<string | null>(null);
  const { t } = useTranslations();

  const parentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const childTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleParentHover = (slug: string | null) => {
    if (slug) {
      clearTimeout(parentTimeoutRef.current!);
      setActiveParent(slug);
    } else {
      parentTimeoutRef.current = setTimeout(() => {
        setActiveParent(null);
        setActiveChild(null);
      }, 50000);
    }
  };

  const handleChildHover = (slug: string | null) => {
    if (slug) {
      clearTimeout(childTimeoutRef.current!);
      setActiveChild(slug);
    } else {
      childTimeoutRef.current = setTimeout(() => {
        setActiveChild(null);
      }, 50000);
    }
  };

  return (
    <div
      className={cn(
        'custom-container',
        'hidden flex-row items-start justify-between gap-8 md:flex',
      )}
    >
      {/* Parent Content Box - Always Visible */}
      <ContentBox
        className={{ wrapper: 'ea-content-box__parent', link: 'text-2xl lg:text-3xl' }}
        items={categories}
        onHover={handleParentHover}
        isAlwaysVisible={true}
        isActive={true}
        activeSlug={activeParent}
        extraElement={{
          name: t('text2263'),
          slug: '/servis-bicyklov',
        }}
      />

      <Separator className="h-[315px] w-[1px] bg-border" />

      {/* Child Content Box */}
      <ContentBox
        className={{ wrapper: 'ea-content-box__child', link: 'text-xl lg:text-2xl' }}
        items={categories.find(item => item.slug === activeParent)?.childs || []}
        onHover={handleChildHover}
        isAlwaysVisible={false}
        isActive={!!activeParent}
        activeSlug={activeChild}
      />

      <Separator className="h-[315px] w-[1px] bg-border" />
      {/* Grandchild Content Box */}
      <ContentBox
        className={{ wrapper: 'ea-content-box__grandchild', link: 'text-base lg:text-lg' }}
        items={
          categories
            ?.find(item => item.slug === activeParent)
            ?.childs?.find(child => child.slug === activeChild)?.childs || []
        }
        onHover={() => {}}
        isAlwaysVisible={false}
        isActive={!!activeChild}
        activeSlug={null}
      />
    </div>
  );
};

const ContentBox = ({
  className,
  items,
  onHover,
  isAlwaysVisible,
  isActive,
  activeSlug,
  extraElement,
}: {
  className: { wrapper?: string; link?: string };
  items: CategoriesEntity[];
  onHover: (slug: string | null) => void;
  isAlwaysVisible: boolean;
  isActive: boolean;
  activeSlug: string | null;
  extraElement?: {
    name: string;
    slug: string;
  };
}) => {
  return (
    <ul
      className={cn(
        'w-full max-w-[485px] bg-slate-200 transition-all duration-300 ease-in-out',
        className.wrapper,
        isAlwaysVisible ? 'block' : '',
        isActive ? 'visible opacity-100' : 'invisible opacity-0',
      )}
      onMouseLeave={() => onHover(null)}
    >
      {items.map(item => (
        <li
          key={item.slug}
          onMouseEnter={() => onHover(item.slug)}
          className={cn(activeSlug === item.slug ? 'bg-primary text-white' : '')}
        >
          <SheetClose asChild>
            <Link prefetchOnHover
              prefetch={false}
              href={ROUTES.CATEGORY({ slug: item.slug })}
              className={cn(
                'flex items-center justify-between bg-white px-4 py-2 font-heading uppercase text-foreground hover:bg-primary hover:text-white',
                className.link,
              )}
            >
              {item.name}
              <MdOutlineKeyboardArrowRight size={20} className="text-white" />
            </Link>
          </SheetClose>
        </li>
      ))}
      {extraElement && (
        <li
          key={extraElement.slug}
          onMouseEnter={() => onHover(extraElement.slug)}
          className={cn(activeSlug === extraElement.slug ? 'bg-primary text-white' : '')}
        >
          <SheetClose asChild>
            <Link prefetchOnHover
              prefetch={false}
              href={extraElement.slug}
              className={cn(
                'flex items-center justify-between bg-white px-4 py-2 font-heading uppercase text-foreground hover:bg-primary hover:text-white',
                className.link,
              )}
            >
              {extraElement.name}
            </Link>
          </SheetClose>
        </li>
      )}
    </ul>
  );
};
